import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { CloseIcon } from "@chakra-ui/icons";
import {
  Image,
  Box,
  Flex,
  WrapItem,
  Wrap,
  Text,
  Skeleton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  InputGroup,
  Input,
  InputRightElement,
} from "@chakra-ui/react";
import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../Store/hooks";
import { ModalsName, setModalOpen } from "../../../Store/ModalsReducer";
import Loader from "../../Helpers/Loader";

const LOAD_MONUMENTS_QUERY = gql`
  query LoadMonuments(
    $category: String!
    $page: Int
    $take: Int
    $name: String
  ) {
    monuments(category: $category, page: $page, take: $take, name: $name) {
      items {
        id
        name
        preview
      }
      total
    }
  }
`;

const LOAD_MONUMENT_QUERY = gql`
  query LoadMonument($id: Int!) {
    monument(id: $id) {
      id
      path3D {
        dirname
        obj
        mtl
      }
    }
  }
`;

type ModelMini = {
  id: string;
  name: string;
  preview: string;
};

const Monument = (
  props: ModelMini & {
    addModel: (
      data: {
        dirname: string;
        obj: string;
        mtl: string;
      },
      category: string,
      id: number
    ) => void;
  }
) => {
  const dispatch = useAppDispatch();
  const [loadMonument, { loading, error, data }] = useLazyQuery(
    LOAD_MONUMENT_QUERY,
    {
      onCompleted: () => {
        dispatch(setModalOpen({ key: ModalsName.ADD, value: false }));
        props.addModel(
          data.monument.path3D,
          "monument",
          parseInt(props.id, 10)
        );
      },
      onError: (err) => {
        console.log(err, error);
      },
    }
  );
  return (
    <WrapItem
      onClick={() =>
        loadMonument({
          variables: {
            id: props.id,
          },
        })
      }
    >
      <Box w={200} h={175} textAlign="center" border="#333 1px solid">
        {loading ? (
          <Flex h={200}>
            <Loader />
          </Flex>
        ) : (
          <>
            <Text w={198} h={25} bg="#333" color="white">
              {props.name}
            </Text>
            <Flex
              w={200}
              h={150}
              align="center"
              alignItems="center"
              justify="center"
            >
              <Image
                src={props.preview}
                alt={props.name}
                maxW={200}
                maxH={140}
                fallback={<Skeleton w={200} h={150} />}
              />
            </Flex>
          </>
        )}
      </Box>
    </WrapItem>
  );
};

const ClearTab = (props: {
  search: string;
  addModel: (
    data: {
      dirname: string;
      obj: string;
      mtl: string;
    },
    category: string,
    id: number
  ) => void;
}) => {
  const [page, setPage] = useState(1);

  const { loading, data, fetchMore } = useQuery(LOAD_MONUMENTS_QUERY, {
    variables: { category: "clear", name: props.search, page },
  });

  const [loadingMoreStatus, setLoading] = useState(false);

  const [search, setSearch] = useState(props.search);

  const loadMore = async (
    _page: number = page + 1,
    _search: string = search
  ) => {
    setLoading(true);
    await fetchMore({
      variables: {
        category: "clear",
        name: _search,
        page: _page,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        console.log(prev, fetchMoreResult);
        if (!prev || Object.keys(prev).length === 0) return fetchMoreResult;
        return {
          ...prev,
          monuments: {
            total: prev.monuments.total,
            items: [
              ...prev.monuments.items,
              ...fetchMoreResult.monuments.items,
            ],
          },
        };
      },
    });
    setPage(page + 1);
    setLoading(false);
  };

  useEffect(() => {
    if (props.search !== search) {
      setSearch(props.search);
      setPage(1);
      loadMore(0, props.search);
    }
    // eslint-disable-next-line
  }, [props.search, search]);

  return (
    <Box>
      {loading && <LoadingMonuments />}
      {!loading && data && (
        <>
          {data && data.monuments.items.length === 0 && (
            <Text align="center">Ничего не найдено!</Text>
          )}
          <Wrap justify="space-evenly">
            {data.monuments.items.map((monument: ModelMini) => (
              <Monument
                {...monument}
                key={`monument${monument.id}`}
                addModel={props.addModel}
              />
            ))}
          </Wrap>
          {data && data.monuments.total > data.monuments.items.length && (
            <LoadMoreButton
              isLoading={loadingMoreStatus}
              loadMore={() => loadMore()}
            />
          )}
        </>
      )}
    </Box>
  );
};

const SavedTab = (props: {
  search: string;
  addModel: (
    data: {
      dirname: string;
      obj: string;
      mtl: string;
    },
    category: string,
    id: number
  ) => void;
}) => {
  const [page, setPage] = useState(1);
  const [loadingMoreStatus, setLoading] = useState(false);
  const [search, setSearch] = useState(props.search);

  const { /*loading, data,*/ fetchMore } = useQuery(LOAD_MONUMENTS_QUERY, {
    variables: { category: "saved", page: 1, name: search },
  });

  useEffect(() => {
    if (props.search !== search) {
      setSearch(props.search);
      setPage(1);
      loadMore(0, props.search);
    }
    // eslint-disable-next-line
  }, [props.search]);

  const loadMore = async (
    _page: number = page + 1,
    _search: string = search
  ) => {
    setLoading(true);
    await fetchMore({
      variables: {
        category: "saved",
        name: _search,
        page: _page,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        console.log(prev, fetchMoreResult);
        if (!prev || Object.keys(prev).length === 0) return fetchMoreResult;
        return {
          ...prev,
          monuments: {
            total: prev.monuments.total,
            items: [
              ...prev.monuments.items,
              ...fetchMoreResult.monuments.items,
            ],
          },
        };
      },
    });
    setPage(page + 1);
    setLoading(false);
  };

  return <Text>Раздел временно недоступен</Text>;
  // return (
  //   <Box>
  //     {loading && <LoadingMonuments />}
  //     {!loading && data && data.monuments && data.monuments.items && (
  //       <>
  //         {data && data.monuments.items.length === 0 && (
  //           <Text align="center">Ничего не найдено!</Text>
  //         )}
  //         <Wrap justify="space-evenly">
  //           {data.monuments.items.map((monument: ModelMini) => (
  //             <Monument
  //               {...monument}
  //               key={`saved-monument${monument.id}`}
  //               addModel={props.addModel}
  //             />
  //           ))}
  //         </Wrap>
  //         {data.monuments.total > data.monuments.items.length && (
  //           <LoadMoreButton
  //             isLoading={loadingMoreStatus}
  //             loadMore={() => loadMore()}
  //           />
  //         )}
  //       </>
  //     )}
  //   </Box>
  // );
};

const PublishedTab = (props: {
  search: string;
  addModel: (
    data: {
      dirname: string;
      obj: string;
      mtl: string;
    },
    category: string,
    id: number
  ) => void;
}) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loadingMore, setLoading] = useState(false);

  const { /*loading, data,*/ fetchMore } = useQuery(LOAD_MONUMENTS_QUERY, {
    variables: { category: "published", page: 1, name: search },
  });

  useEffect(() => {
    if (props.search !== search) {
      setSearch(props.search);
      setPage(1);
      loadMore(0, props.search);
    }
    // eslint-disable-next-line
  }, [props.search]);

  const loadMore = async (
    _page: number = page + 1,
    _search: string = search
  ) => {
    setLoading(true);
    await fetchMore({
      variables: {
        category: "published",
        name: _search,
        page: _page,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        console.log(prev, fetchMoreResult);
        if (!prev || Object.keys(prev).length === 0) return fetchMoreResult;
        return {
          ...prev,
          monuments: {
            total: prev.monuments.total,
            items: [
              ...prev.monuments.items,
              ...fetchMoreResult.monuments.items,
            ],
          },
        };
      },
    });
    setPage(page + 1);
    setLoading(false);
  };
return <Text>Раздел временно недоступен</Text>;
  // return (
  //   <Box>
  //     {loading && <LoadingMonuments />}
  //     {!loading && data && (
  //       <>
  //         {data && data.monuments.items.length === 0 && (
  //           <Text align="center">Ничего не найдено!</Text>
  //         )}
  //         <Wrap justify="space-evenly">
  //           {data.monuments.items.map((monument: ModelMini) => (
  //             <Monument
  //               {...monument}
  //               key={`published-monument${monument.id}`}
  //               addModel={props.addModel}
  //             />
  //           ))}
  //         </Wrap>
  //         {data.monuments.total > data.monuments.items.length && (
  //           <LoadMoreButton
  //             isLoading={loadingMore}
  //             loadMore={() => loadMore()}
  //           />
  //         )}
  //       </>
  //     )}
  //   </Box>
  // );
};

const Monuments = (props: {
  addModel: (
    data: {
      dirname: string;
      obj: string;
      mtl: string;
    },
    category: string,
    id: number
  ) => void;
}) => {
  const onSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    setSearch(searchInput);
  };
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const user = useAppSelector((state) => state.global.user);
  return (
    <>
      <Flex justify="flex-start" marginBottom={10}>
        <Box w="30%">
          <form onSubmit={onSubmit}>
            <InputGroup size="lg" variant="obeliski">
              <InputGroup>
                <Input
                  h={35}
                  fontSize={14}
                  borderTopRightRadius={0}
                  borderBottomRightRadius={0}
                  value={searchInput}
                  onChange={(ev: ChangeEvent<HTMLInputElement>) =>
                    setSearchInput(ev.target.value)
                  }
                />
                {searchInput.length > 0 && (
                  <InputRightElement>
                    <CloseIcon
                      _hover={{ cursor: "pointer" }}
                      onClick={() => {
                        setSearch("");
                        setSearchInput("");
                      }}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <Button variant="editor" onClick={() => setSearch(searchInput)}>
                Поиск
              </Button>
            </InputGroup>
          </form>
        </Box>
      </Flex>
      <Tabs isLazy isFitted variant="obeliski">
        <TabList>
          <Tab>Модели</Tab>
          {user && <Tab>Мои эскизы</Tab>}
          <Tab>Опубликованные эскизы</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <ClearTab addModel={props.addModel} search={search} />
          </TabPanel>
          {user && (
            <TabPanel>
              <SavedTab addModel={props.addModel} search={search} />
            </TabPanel>
          )}
          <TabPanel>
            <PublishedTab addModel={props.addModel} search={search} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
};

const LoadMoreButton = (props: {
  loadMore: () => void;
  isLoading: boolean;
}) => {
  return (
    <Flex p={5}>
      <Button isFullWidth isLoading={props.isLoading} onClick={props.loadMore}>
        Еще
      </Button>
    </Flex>
  );
};

const LoadingMonuments = () => {
  return (
    <Wrap justify="space-evenly">
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
      <Skeleton w={200} h={175}></Skeleton>
    </Wrap>
  );
};

export default Monuments;
